/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IModule } from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import isMatch from 'lodash/isMatch';
import { observer } from 'mobx-react';
import * as React from 'react';

import { getModuleStates, updateModuleStates } from './module-state';
import { IModuleState, IModuleStateManager, IModuleStateProps, IModuleStates } from './module-state.data';

export interface IProps extends IModule, IModuleStateProps {
    enableControl?: boolean;
}

const sectionContainerModuleId = 'section-container';
const paymentInstrumentModuleId = 'payment-instrument';

const withModuleState = <P extends IProps>(WrappedComponent: React.ComponentType<P>): React.ComponentType<P> => {
    /**
     *
     * ModuleState component.
     * @extends {React.Component<P>}
     */
    @observer
    class ModuleState extends React.Component<P> {
        constructor(props: P) {
            super(props);
            this.initializeState();
        }

        public shouldComponentUpdate(nextProps: IModuleStateProps): boolean {
            if (this.props === nextProps) {
                return false;
            }
            return true;
        }

        public render(): JSX.Element | null {
            const { id } = this.props;
            return <WrappedComponent {...this.props} moduleState={this.getModuleStateManager(id)} />;
        }

        private readonly initializeState = (): void => {
            const { id, typeName, context } = this.props;
            const states = getModuleStates(context.actionContext);
            if (!states) {
                this.props.telemetry.error('withModuleState initializeState() - states not found');
                return;
            }

            if (states[id]) {
                // State has been initialized
                return;
            }

            updateModuleStates(
                {
                    ...states,
                    [id]: {
                        id,
                        typeName,
                        hasInitialized: false,
                        hasError: false,
                        isRequired: true,
                        isCancellable: true,
                        isSubmitContainer: false,
                        status: undefined,
                        childIds: []
                    }
                },
                context.actionContext
            );
        };

        /**
         * GetModuleStateManager
         * Get module state manager by id.
         * @param id
         */
        private readonly getModuleStateManager = (id: string): IModuleStateManager => {
            const moduleState = this.get()[id];
            return {
                ...moduleState!,
                hasInitialized: this.validate(id, { hasInitialized: true }, true), // All has initialized is initialized
                hasError: this.validate(id, { hasError: true }), // Partial has error is error
                isReady: this.validate(id, { status: 'ready' }, true, true), // All ready is ready (exclued disabled and skipped)
                isUpdating: this.validate(id, { status: 'updating' }), // Partial updating is updating
                isPending: this.validate(id, { status: 'pending' }), // Partial pending is pending
                isSkipped: this.validate(id, { status: 'skipped' }, true, true), // All skipped is skipped (exclued disabled)
                isDisabled: this.validate(id, { status: 'disabled' }, true), // All disabled is disabled
                isCancelAllowed: this.validate(id, { isCancellable: true }, true, true), // Partial not allowed is not allowed
                shouldSubmitContainer: this.validate(id, { isSubmitContainer: true }), // Partial submit is submit.
                hasExternalSubmitGroup: this.hasExternalSubmitGroup(),
                hasModuleState: this.hasModuleState(id),
                setIsRequired: (value: boolean): void => {
                    this.update(id, { isRequired: value });
                },
                setIsCancellable: (value: boolean): void => {
                    this.update(id, { isCancellable: value });
                },
                setIsSubmitContainer: (value: boolean): void => {
                    this.update(id, { isSubmitContainer: value });
                },
                setHasError: (value: boolean): void => {
                    this.update(id, { hasError: value });
                },
                onReady: (): void => {
                    this.update(id, { status: 'ready' });
                },
                onUpdating: (): void => {
                    this.update(id, { status: 'updating' });
                },
                onPending: (): void => {
                    this.update(id, { status: 'pending' });
                },
                onSkip: (): void => {
                    this.update(id, { status: 'skipped' });
                },
                onDisable: (): void => {
                    this.update(id, { status: 'disabled' });
                },
                getModule: (moduleId: string): IModuleStateManager => this.getModuleStateManager(moduleId),
                getModuleByTypeName: (typeName: string): IModuleStateManager => this.getModuleStateManagerByTypeName(typeName),
                init: (options?: Partial<IModuleState>): void => {
                    if (moduleState?.hasInitialized) {
                        // State has been initialized
                        return;
                    }
                    this.update(id, {
                        hasInitialized: true,
                        ...options
                    });
                }
            };
        };

        /**
         * GetModuleStateManagerByTypeName
         * Get module state manager by type name.
         * @param typeName
         */
        private readonly getModuleStateManagerByTypeName = (typeName: string): IModuleStateManager => {
            const moduleStates = getModuleStates(this.props.context.actionContext);
            const moduleState = Object.values(moduleStates).find(_moduleState => _moduleState?.typeName === typeName);
            return this.getModuleStateManager((moduleState && moduleState.id) || '');
        };

        /**
         * Get
         * Get all module states.
         */
        private readonly get = (): IModuleStates => {
            return getModuleStates(this.props.context.actionContext);
        };

        /**
         * Update
         * Update module state.
         * @param id
         * @param value
         */
        private readonly update = (id: string, value: Partial<IModuleState>): void => {
            // Console.log('withModuleState - update', id, value);
            const modules = this.get();
            if (!modules[id]) {
                this.props.telemetry.error(`withModuleState update() - Module state with id ${id} is not found.`);
                return;
            }
            modules[id] = {
                ...modules[id]!,
                ...value
            };
        };

        private readonly _validateLeaf = (id: string, source: Partial<IModuleState>): boolean => {
            const modules = this.get();
            const module = modules[id];
            if (!module) {
                return false;
            }
            return isMatch(module, source);
        };

        private readonly _validateContainer = (
            id: string,
            source: Partial<IModuleState>,
            allMatched?: boolean,
            skipSkippableItem?: boolean
        ): boolean => {
            const modules = this.get();
            const module = modules[id];
            if (!module) {
                // Module doesn't has module state
                return !!allMatched;
            }

            if (skipSkippableItem && (module.status === 'disabled' || module.status === 'skipped')) {
                // Skip disabled or skipped modules
                return !!allMatched;
            }

            // It is leaf module
            if (!module.childIds || module.childIds.length === 0) {
                return this._validateLeaf(id, source);
            }

            let childIds = module.childIds;

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Do not need type check for appsettings
            if (this.props.context.app.config.shouldEnableSinglePaymentAuthorizationCheckout) {
                // For new checkout flow, we bypass the isReady check for payment section container to enable the place order button.
                childIds = childIds.filter(childId => !this._isPaymentSectionContainer(childId));
            }

            // It is container module
            const method = allMatched ? 'every' : 'some';
            return childIds[method](childId => this._validateContainer(childId, source, allMatched, skipSkippableItem));
        };

        /**
         * Check if it is a section container with payment module.
         * @param moduleId -- The id of the module.
         * @returns If it is a section container with payment module.
         */
        private readonly _isPaymentSectionContainer = (moduleId: string): boolean => {
            if (!moduleId.includes(sectionContainerModuleId)) {
                return false;
            }

            const modules = this.get();
            const module = modules[moduleId];

            if (module && ArrayExtensions.hasElements(module.childIds.filter(childId => childId.includes(paymentInstrumentModuleId)))) {
                return true;
            }

            return false;
        };

        /**
         * Validate
         * Validate current module and all its child module match the provided condition.
         * @param id
         * @param source
         * @param allMatched
         * @param skipSkippableItem
         */
        private readonly validate = (id: string, source: Partial<IModuleState>, allMatched?: boolean, skipSkippableItem?: boolean): boolean => {
            const modules = this.get();
            const module = modules[id];
            if (!module) {
                return false;
            }

            // It is leaf module
            if (!module.childIds || module.childIds.length === 0) {
                return this._validateLeaf(id, source);
            }

            // It is container module
            return this._validateContainer(id, source, allMatched, skipSkippableItem);
        };

        /**
         * HasExternalSubmitGroup
         * Module will use external submit group.
         */
        private readonly hasExternalSubmitGroup = (): boolean => {
            return !!this.props.enableControl;
        };

        /**
         * HasModuleState
         * Module is using module state manager.
         * @param id
         */
        private readonly hasModuleState = (id: string): boolean => {
            const modules = this.get();
            const module = modules[id];
            return !!module;
        };
    }

    return ModuleState;
};

export default withModuleState;
